import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "dashboard-wrapper" }
const _hoisted_2 = { class: "section-header" }
const _hoisted_3 = { class: "chart-container" }
const _hoisted_4 = {
  key: 1,
  class: "chart-placeholder"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "stat-label" }
const _hoisted_7 = { class: "stat-value text-primary" }
const _hoisted_8 = { class: "stat-label" }
const _hoisted_9 = { class: "stat-value text-success" }
const _hoisted_10 = {
  key: 1,
  class: "table-section"
}

import { ref, computed, reactive, onMounted } from 'vue'
import { checkMobile } from '../../utils/utils'
import ChartLine from '../atoms_molecules/ChartLine.vue'
import UserTrackingData from '../../../api/v2/models/user_tracking_data'
import LearningStatsData from '../../../api/v2/models/learning_stats_data'
import api from '../../../api/v2/api'
import AppDateRangePicker from '../atoms_molecules/AppDateRangePicker.vue'
import customRangeFn, { localeData } from '../../../../helpers/date-range'
import { subMonths, format } from 'date-fns'
import { ROUTER_NAME } from '../../router'
import { convertMinutesToTimeJp } from '../../../../lib/convert'
import { userState } from '../../stores/user'

// 折れ線グラフで使用するラベル名
const LEARN_TIME_TEXT = '学習時間'


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardChart',
  props: {
    bannerHtml: { default: '' }
  },
  emits: ["rangeDate"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// レスポンシブ判定 (phải khai báo trước dateRange)
const isMobile = ref(checkMobile())

// 日付範囲（初期値：モバイルは7日、PCは1ヶ月）
const dateRange = ref({
  startDate: isMobile.value ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : subMonths(new Date(), 1),
  endDate: new Date()
})

// DateRangePicker用
const customRange = customRangeFn()

// トラッキング（学習時間や平均など）用データ（旧方式）
const userTrackingData = ref<UserTrackingData | null>(null)

// /api/v2/activities から取得するアクティビティの配列（旧方式）
const activitiesData = ref<any[]>([])

// 新方式（learning_stats）のデータ。LEARNING_STATS_READ_SOURCE=new のときのみ使用
const learningStatsData = ref<LearningStatsData | null>(null)
const useNewStats = computed(() => userState.user?.learningStatsReadSource === 'new')

// 折れ線グラフで使うデータ
const data = reactive<{
  labels: any[]
  datasets: any[]
}>({
  labels: [],
  datasets: []
})

// Chart.js のオプション設定
const chartOptions = {
  elements: {
    line: {
      tension: 0
    }
  },
  tooltips: {
    callbacks: {
      label: (tooltipItem: any, chartData: any) => {
        const label = chartData.datasets[tooltipItem.datasetIndex].label || ''
        const value = Math.round(tooltipItem.yLabel * 100) / 100
        if (label === LEARN_TIME_TEXT) {
          return `${label}: ${convertMinutesToTimeJp(value)}`
        } else {
          return `${label}: ${value}`
        }
      }
    }
  },
  scales: {
    x: {
      display: true
    },
    y: {
      display: true,
      ticks: {
        stepSize: 60
      }
    }
  }
}

// テーブルのカラム定義
const headers = [
  {
    title: '学習日',
    align: 'start' as const,
    sortable: false,
    key: 'label'
  },
  { title: '学習時間', key: 'trackingMinutes', sortable: true },
  { title: '学習完了数', key: 'activity', sortable: true }
]

/**
 * テーブルに表示するデータ
 */
const tableData = computed(() => {
  if (useNewStats.value) {
    if (!learningStatsData.value || !userTrackingData.value) return []

    return userTrackingData.value.labels
      .map((label, index) => {
        const dateStr = format(new Date(label), 'YYYY-MM-DD')
        const isLegacyEstimate = learningStatsData.value!.isLegacyEstimate(dateStr)
        const day = learningStatsData.value!.days.find((item) => item.date === dateStr)
        const trackingMinutes = isLegacyEstimate || userTrackingData.value!.isManuallyChanged(dateStr)
          ? userTrackingData.value!.trackingUserValues[index]
          : day?.totalActiveMinutes ?? 0
        const activity = isLegacyEstimate
          ? activitiesData.value.filter((item) => format(new Date(item.createdAt), 'YYYY-MM-DD') === dateStr).length
          : day?.completedLessonCount ?? 0
        return {
          label: dateStr,
          tracking: convertMinutesToTimeJp(trackingMinutes),
          trackingMinutes,
          activity,
          isLegacyEstimate
        }
      })
      .reverse()
  }

  if (!userTrackingData.value) {
    return []
  }

  return userTrackingData.value.labels
    .map((label, index) => {
      // ラベル(例: '2025-02-16')を 'YYYY-MM-DD' に変換
      const dateStr = format(new Date(label), 'YYYY-MM-DD')

      // 該当の日付にマッチするアクティビティを全部集める
      const activitiesForDate = activitiesData.value.filter((act: any) => {
        const actDateStr = format(new Date(act.createdAt), 'YYYY-MM-DD')
        return actDateStr === dateStr
      })

      // その日の学習完了数
      const activityCount = activitiesForDate.length

      // 学習時間(分)を取り出す
      const minutes = userTrackingData.value!.trackingUserValues[index]

      return {
        label: label,
        tracking: convertMinutesToTimeJp(minutes),
        trackingMinutes: minutes,
        activity: activityCount,
        isLegacyEstimate: false
      }
    })
    .reverse()
})

/**
 * 平均学習時間
 */
const averageTrackingTime = computed(() => {
  if (!tableData.value.length) return '0時間0分'

  const totalMinutes = tableData.value.reduce((acc, row) => {
    return acc + row.trackingMinutes
  }, 0)

  const avgMinutes = totalMinutes / tableData.value.length
  return convertMinutesToTimeJp(Math.round(avgMinutes))
})

/**
 * 平均学習完了数
 */
const averageActivityCount = computed(() => {
  if (!tableData.value.length) return '0'

  const totalCount = tableData.value.reduce((acc, row) => {
    return acc + row.activity
  }, 0)

  const avg = totalCount / tableData.value.length
  return avg.toFixed(1)
})

/**
 * 日付範囲が変わるたびにAPIを呼び出す
 */
const updateDateRange = async () => {
  if (!userState.user) await userState.getUser()
  // 親へ現在のdateRangeを通知
  emit('rangeDate', dateRange.value)

  let labels: any[]
  let values: number[]

  if (useNewStats.value) {
    const [newStats, legacyStats, legacyActivities] = await Promise.all([
      api.learningStats.dailySeries(dateRange.value),
      api.tracking.get(dateRange.value),
      api.activity.get(0, dateRange.value)
    ])
    learningStatsData.value = newStats
    userTrackingData.value = legacyStats
    activitiesData.value = legacyActivities.activities || []
    labels = legacyStats.labels
    values = legacyStats.labels.map((label, index) => {
      const dateStr = format(new Date(label), 'YYYY-MM-DD')
      if (newStats.isLegacyEstimate(dateStr) || legacyStats.isManuallyChanged(dateStr)) {
        return legacyStats.trackingUserValues[index]
      }
      return newStats.days.find((day) => day.date === dateStr)?.totalActiveMinutes ?? 0
    })
  } else {
    // 1. トラッキングデータ取得（グラフに使用）
    userTrackingData.value = await api.tracking.get(dateRange.value)

    // 2. アクティビティデータ取得（テーブルに使用）
    const response = await api.activity.get(0, dateRange.value)
    activitiesData.value = response.activities || []
    learningStatsData.value = null

    labels = userTrackingData.value.labels
    values = userTrackingData.value.trackingUserValues
  }

  // グラフ描画用のデータをセット
  data.labels = labels
  data.datasets = [
    {
      label: LEARN_TIME_TEXT,
      borderColor: 'rgb(67, 67, 72)',
      data: values,
      fill: false,
      pointBorderWidth: 2
    }
  ]
}

onMounted(() => {
  updateDateRange()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_row, { class: "dashboard-content" }, {
      default: _withCtx(() => [
        _createCommentVNode(" 左側: 日付範囲のピッカーとグラフ "),
        _createVNode(_component_v_col, {
          cols: "12",
          lg: "8",
          class: "chart-section"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "section-title" }, "学習データ", -1 /* CACHED */)),
              _createVNode(AppDateRangePicker, {
                onUpdate: updateDateRange,
                modelValue: dateRange.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
                opens: "right",
                "append-to-body": true,
                "locale-data": _unref(localeData),
                ranges: _unref(customRange)
              }, null, 8 /* PROPS */, ["modelValue", "locale-data", "ranges"])
            ]),
            _createElementVNode("div", _hoisted_3, [
              (data.datasets.length > 0)
                ? (_openBlock(), _createBlock(ChartLine, {
                    key: 0,
                    width: 800,
                    height: isMobile.value ? 300 : 280,
                    data: data,
                    chartOptions: chartOptions
                  }, null, 8 /* PROPS */, ["height", "data"]))
                : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(_component_v_icon, {
                      size: "48",
                      color: "grey-lighten-1"
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode("mdi-chart-line", -1 /* CACHED */)
                      ]))]),
                      _: 1 /* STABLE */
                    }),
                    _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-grey" }, "データを読み込み中...", -1 /* CACHED */))
                  ]))
            ])
          ]),
          _: 1 /* STABLE */
        }),
        _createCommentVNode(" 右側: カード(平均時間など) と テーブル "),
        (userTrackingData.value)
          ? (_openBlock(), _createBlock(_component_v_col, {
              key: 0,
              cols: "12",
              lg: "4",
              class: "stats-section"
            }, {
              default: _withCtx(() => [
                (__props.bannerHtml)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      innerHTML: __props.bannerHtml,
                      class: "mb-3"
                    }, null, 8 /* PROPS */, _hoisted_5))
                  : _createCommentVNode("v-if", true),
                _createVNode(_component_v_row, { class: "stats-cards" }, {
                  default: _withCtx(() => [
                    _createCommentVNode(" 平均学習時間カード "),
                    _createVNode(_component_v_col, { cols: "6" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "stat-card",
                          elevation: "0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "stat-card-content" }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_6, [
                                  _createVNode(_component_v_icon, {
                                    size: "18",
                                    color: "primary",
                                    class: "me-1"
                                  }, {
                                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                      _createTextVNode("mdi-clock-outline", -1 /* CACHED */)
                                    ]))]),
                                    _: 1 /* STABLE */
                                  }),
                                  _cache[5] || (_cache[5] = _createTextVNode(" 平均学習時間 ", -1 /* CACHED */))
                                ]),
                                _createElementVNode("div", _hoisted_7, _toDisplayString(averageTrackingTime.value), 1 /* TEXT */),
                                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "stat-hint" }, "1日あたりの学習時間", -1 /* CACHED */))
                              ]),
                              _: 1 /* STABLE */
                            })
                          ]),
                          _: 1 /* STABLE */
                        })
                      ]),
                      _: 1 /* STABLE */
                    }),
                    _createCommentVNode(" 平均学習完了数カード "),
                    _createVNode(_component_v_col, { cols: "6" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "stat-card",
                          elevation: "0"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "stat-card-content" }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_8, [
                                  _createVNode(_component_v_icon, {
                                    size: "18",
                                    color: "success",
                                    class: "me-1"
                                  }, {
                                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                      _createTextVNode("mdi-check-circle-outline", -1 /* CACHED */)
                                    ]))]),
                                    _: 1 /* STABLE */
                                  }),
                                  _cache[8] || (_cache[8] = _createTextVNode(" 平均完了数 ", -1 /* CACHED */))
                                ]),
                                _createElementVNode("div", _hoisted_9, [
                                  _createTextVNode(_toDisplayString(averageActivityCount.value) + " ", 1 /* TEXT */),
                                  _cache[9] || (_cache[9] = _createElementVNode("span", { class: "stat-unit" }, "個", -1 /* CACHED */))
                                ]),
                                _cache[10] || (_cache[10] = _createElementVNode("div", { class: "stat-hint" }, "1日あたりの完了数", -1 /* CACHED */))
                              ]),
                              _: 1 /* STABLE */
                            })
                          ]),
                          _: 1 /* STABLE */
                        })
                      ]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }),
                _createCommentVNode(" テーブル表示 "),
                (!isMobile.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createVNode(_component_v_data_table, {
                        headers: headers,
                        items: tableData.value,
                        "items-per-page": 5,
                        class: "stats-table",
                        density: "compact"
                      }, {
                        "item.label": _withCtx(({ item }) => [
                          _createTextVNode(_toDisplayString(item.label) + " ", 1 /* TEXT */),
                          (item.isLegacyEstimate)
                            ? (_openBlock(), _createBlock(_component_v_chip, {
                                key: 0,
                                size: "x-small",
                                color: "grey-lighten-1",
                                class: "ms-1",
                                title: "この日付は新しい計測方式の開始日より前のため、旧方式による推定値を表示しています"
                              }, {
                                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                  _createTextVNode(" 旧方式による推定値 ", -1 /* CACHED */)
                                ]))]),
                                _: 1 /* STABLE */
                              }))
                            : _createCommentVNode("v-if", true)
                        ]),
                        "item.trackingMinutes": _withCtx(({ item }) => [
                          _createTextVNode(_toDisplayString(item.tracking), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["items"])
                    ]))
                  : _createCommentVNode("v-if", true)
              ]),
              _: 1 /* STABLE */
            }))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

})